import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { v4 as uuid } from 'uuid'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import DragAndDropListItem from '../../../GroupingProvider/DragAndDropListItem'
import { SCOPE, toggleRowScope } from './metadataScope'

const GRID_COLS = 'minmax(140px, 1.2fr) minmax(140px, 1.2fr) minmax(100px, 0.8fr) 130px 90px 40px'
const HEADER_GRID_COLS = '40px ' + GRID_COLS

const useStyles = makeStyles(() => ({
  grid: {
    fontSize: 'var(--font-size-xs)'
  },
  headerRow: {
    display: 'grid',
    alignItems: 'center',
    backgroundColor: 'var(--color-neutral-700)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 0',
    gap: '4px',
    borderRadius: '4px 4px 0 0'
  },
  headerCell: {
    padding: '0 8px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerCellCenter: {
    padding: '0 8px',
    textAlign: 'center'
  },
  dataRow: {
    display: 'grid',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0',
    borderBottom: '1px solid var(--color-neutral-200)',
    width: '100%',
    '&:nth-child(even)': {
      backgroundColor: 'var(--color-neutral-50, #fafafa)'
    }
  },
  inputCell: {
    padding: '0 8px'
  },
  cellCenter: {
    padding: '0 8px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  scopeCell: {
    padding: '0 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: 'var(--font-size-xs)'
  },
  scopeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    userSelect: 'none',
    '& .MuiCheckbox-root': {
      padding: '2px'
    }
  },
  dragItem: {
    padding: '0 !important',
    '& > div:first-child': {
      marginRight: '0 !important'
    }
  },
  emptyRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    padding: '16px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  footerControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: '8px'
  },
  formatHint: {
    fontSize: 'var(--font-size-xxs, 0.625rem)',
    color: 'var(--text-color-subtle)',
    marginTop: '4px'
  }
}))

function makeBlankRow () {
  return {
    id: uuid(),
    label: '',
    accessor: '',
    format: null,
    hideWhenEmpty: false,
    scope: SCOPE.BOTH
  }
}

// Some persisted rows pre-date the `id` field — backfill a stable id so DnD
// has something to key on without forcing the admin to re-save. Also ensures
// every row carries a `scope` so the unified editor never has to guard for
// undefined scope on a freshly-restored default.
function withIds (rows, scope = SCOPE.ITEM) {
  return rows.map(row => ({
    ...row,
    id: row.id || uuid(),
    scope: row.scope || scope
  }))
}

function MetadataRowsEditor ({ value, onChange, defaults, showRestoreDefaults, emptyMessage }) {
  const classes = useStyles()

  const rows = useMemo(() => (Array.isArray(value) ? value : []), [value])

  const handleFieldChange = useCallback((index, field, next) => {
    const updated = rows.map((row, i) => i === index ? { ...row, [field]: next } : row)
    onChange(updated)
  }, [rows, onChange])

  const handleRemoveRow = useCallback((index) => {
    onChange(rows.filter((_, i) => i !== index))
  }, [rows, onChange])

  const handleAddRow = useCallback(() => {
    onChange([...rows, makeBlankRow()])
  }, [rows, onChange])

  const handleRestoreDefaults = useCallback(() => {
    onChange(withIds(defaults, SCOPE.ITEM))
  }, [defaults, onChange])

  const handleScopeToggle = useCallback((index, side, checked) => {
    const nextScope = toggleRowScope(rows[index]?.scope || SCOPE.BOTH, side, checked)
    const updated = rows.map((row, i) => i === index ? { ...row, scope: nextScope } : row)
    onChange(updated)
  }, [rows, onChange])

  const handleClearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  const onMoveItem = useCallback((dragIndex, hoverIndex) => {
    const next = [...rows]
    const dragged = next[dragIndex]
    next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, dragged)
    onChange(next)
  }, [rows, onChange])

  return (
    <div>
      <div className={classes.grid}>
        <div className={classes.headerRow} style={{ gridTemplateColumns: HEADER_GRID_COLS }}>
          <div className={classes.headerCell} />
          <div className={classes.headerCell}>Label</div>
          <div className={classes.headerCell}>Accessor</div>
          <div className={classes.headerCell}>Format</div>
          <div className={classes.headerCell}>Show On</div>
          <div className={classes.headerCellCenter}>Hide If Empty</div>
          <div className={classes.headerCell} />
        </div>

        <DndProvider backend={HTML5Backend}>
          {rows.map((row, index) => (
            <DragAndDropListItem
              id={row.id ?? `row-${index}`}
              key={row.id ?? `row-${index}`}
              index={index}
              onMoveItem={onMoveItem}
              size='small'
              className={classes.dragItem}
            >
              <div className={classes.dataRow} style={{ gridTemplateColumns: GRID_COLS }}>
                <div className={classes.inputCell}>
                  <SydInput
                    value={row.label || ''}
                    onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                    size='sm'
                    minWidth='0'
                    placeholder='e.g. Next Review Date'
                  />
                </div>
                <div className={classes.inputCell}>
                  <SydInput
                    value={row.accessor || ''}
                    onChange={(e) => handleFieldChange(index, 'accessor', e.target.value)}
                    size='sm'
                    minWidth='0'
                    placeholder='e.g. metadata.nextReviewDate'
                  />
                </div>
                <div className={classes.inputCell}>
                  <SydInput
                    value={row.format || ''}
                    onChange={(e) => {
                      const raw = e.target.value
                      handleFieldChange(index, 'format', raw.trim() === '' ? null : raw)
                    }}
                    size='sm'
                    minWidth='0'
                    placeholder='e.g. date'
                  />
                </div>
                <div className={classes.scopeCell}>
                  <label className={classes.scopeOption}>
                    <Checkbox
                      size='small'
                      color='primary'
                      disableRipple
                      checked={row.scope === SCOPE.ITEM || row.scope === SCOPE.BOTH}
                      onChange={(e) => handleScopeToggle(index, SCOPE.ITEM, e.target.checked)}
                    />
                    Items
                  </label>
                  <label className={classes.scopeOption}>
                    <Checkbox
                      size='small'
                      color='primary'
                      disableRipple
                      checked={row.scope === SCOPE.CATEGORY || row.scope === SCOPE.BOTH}
                      onChange={(e) => handleScopeToggle(index, SCOPE.CATEGORY, e.target.checked)}
                    />
                    Categories
                  </label>
                </div>
                <div className={classes.cellCenter}>
                  <Switch
                    checked={!!row.hideWhenEmpty}
                    onChange={(e) => handleFieldChange(index, 'hideWhenEmpty', e.target.checked)}
                  />
                </div>
                <div className={classes.cellCenter}>
                  <IconButton size='small' onClick={() => handleRemoveRow(index)} aria-label='Remove row'>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </div>
              </div>
            </DragAndDropListItem>
          ))}
        </DndProvider>

        {rows.length === 0 ? (
          <div className={classes.emptyRow}>
            {emptyMessage}
          </div>
        ) : null}
      </div>

      <div className={classes.formatHint}>
        Format accepts shared aliases (<code>date</code>, <code>currency</code>, <code>percentage</code>, <code>number</code>), raw numeral.js strings (<code>0,0.00</code>), dayjs prefixes (<code>@M/D/YYYY</code>), or <code>planDate</code> for the legacy MM/DD/YY style. Leave blank for the raw value.
      </div>

      <div className={classes.footerControls}>
        <SydButton onClick={handleAddRow} icon='add' size='sm'>
          Add Row
        </SydButton>
        {showRestoreDefaults ? (
          <SydButton onClick={handleRestoreDefaults} variant='secondary' size='sm'>
            Restore Defaults
          </SydButton>
        ) : null}
        {rows.length > 0 ? (
          <SydButton onClick={handleClearAll} variant='secondary' size='sm'>
            Clear All
          </SydButton>
        ) : null}
      </div>
    </div>
  )
}

MetadataRowsEditor.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  defaults: PropTypes.array,
  showRestoreDefaults: PropTypes.bool,
  emptyMessage: PropTypes.string
}

MetadataRowsEditor.defaultProps = {
  value: [],
  defaults: [],
  showRestoreDefaults: false,
  emptyMessage: 'No rows configured. Click "Add Row" to start.'
}

export default MetadataRowsEditor
